import type { UserDto } from "../../shared/types/userTypes";
import { useEffect, type FC } from "react";

import { Card, CircularProgress, Stack, Typography } from "@mui/material";

import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { UserManager } from "../../services/api/UserManager";
import CustomButton from "../../components/CustomButton";
import { formatNumberWithSpaces } from "../../utils/helpers/formatNumberWithSpaces";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { ErrorCategory, logError } from "../../services/api/Analytics";

type ProductData = {
  id: string;
  name: string;
  description: string | null;
  prices: {
    id: string;
    currency: string;
    amount: number | null;
    interval: string | null;
    country: string | null;
    extraCredits: string | null;
  }[];
}[];

export const ExtraCreditsPage: FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const userState = location.state as { user: UserDto };

  const [loading, setLoading] = useState<boolean>(true);
  const [productData, setProductData] = useState<ProductData>();

  const smallCreditPlan = productData?.find(
    item => item.name === "Credits small",
  )?.prices[0];

  const mediumCreditPlan = productData?.find(
    item => item.name === "Credits medium",
  )?.prices[0];

  const largeCreditPlan = productData?.find(
    item => item.name === "Credits large",
  )?.prices[0];

  const creditPlans = [
    {
      title: "SMALL",
      price: new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: smallCreditPlan?.currency.toUpperCase() ?? "czk",
      }).format((smallCreditPlan?.amount ?? 0) / 100),
      views: formatNumberWithSpaces(smallCreditPlan?.extraCredits),
      onClick: () => updateExtraCredits("Credits small"),
    },
    {
      title: "MEDIUM",
      price: new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: mediumCreditPlan?.currency.toUpperCase() ?? "czk",
      }).format((mediumCreditPlan?.amount ?? 0) / 100),
      views: formatNumberWithSpaces(mediumCreditPlan?.extraCredits),
      onClick: () => updateExtraCredits("Credits medium"),
    },
    {
      title: "LARGE",
      price: new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: largeCreditPlan?.currency.toUpperCase() ?? "czk",
      }).format((largeCreditPlan?.amount ?? 0) / 100),
      views: formatNumberWithSpaces(largeCreditPlan?.extraCredits),
      onClick: () => updateExtraCredits("Credits large"),
    },
  ];

  const updateExtraCredits = async (
    creditPlan: "Credits large" | "Credits medium" | "Credits small",
  ) => {
    if (!userState) {
      return;
    }

    if (!creditPlan) {
      return;
    }

    // Call /user/credits/update/usa for USA, /user/credits/update for EU
    const region = process.env.REACT_APP_DESTINATION === "USA" ? "usa" : undefined;

    try {
      const checkoutRes = await UserManager.getManager().updateUserExtraCredits(
        {
          ...userState.user,
          pricingPlanName: creditPlan,
        },
        region
      );

      if (checkoutRes?.sessionUrl) {
        window.location.href = checkoutRes.sessionUrl;
      } else {
        throw new Error("Checkout session URL was not returned by the server");
      }
    } catch (e: any) {
      // Surface the failure to the user instead of failing silently, and persist it for the
      // Admin panel error log.
      showFlashMessageWithTimeout(
        dispatch,
        t("extraCreditsPage.purchaseError") ||
          "We couldn't start the payment. Please try again later or contact support.",
        "error",
      );
      logError(
        ErrorCategory.Payment,
        "Extra credits purchase failed",
        {
          creditPlan,
          region: region ?? "eu",
          userId: userState.user?.id,
          error: e?.toString?.() ?? String(e),
        },
      );
    }
  };

  useEffect(() => {
    const getProductData = async () => {
      setLoading(true);

      // Call /credits/products/usa for USA, /credits/products for EU
      const region = process.env.REACT_APP_DESTINATION === "USA" ? "usa" : undefined;
      const products = await UserManager.getManager().getCreditProducts(region);

      return products;
    };

    getProductData().then(data => {
      setLoading(false);
      setProductData(data as ProductData);
    });
  }, []);

  if (loading) {
    return (
      <Stack
        justifyContent='center'
        alignItems='center'
        sx={{
          width: 1,
          height: 100,
        }}
      >
        <CircularProgress style={{ color: theme.palette.main.primary.o }} />
      </Stack>
    );
  }

  return (
    <Stack sx={{ px: 3, py: 2, gap: 5 }}>
      <Stack>
        <Typography
          sx={{
            color: theme.palette.main.primary.o,
            fontSize: 16,
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Typography>
        <Typography sx={{ color: theme.palette.natural.t1, fontWeight: "600" }}>
          {t("subscriptions.portableCreditsDescription")}
        </Typography>
      </Stack>
      <Stack
        direction='row'
        spacing={3}
        justifyContent='center'
        flexWrap='wrap'
      >
        {creditPlans.map(plan => (
          <Card
            key={plan.title}
            sx={{
              minWidth: 250,
              p: 2,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant='h6' fontWeight='bold' gutterBottom>
              {plan.title}
            </Typography>
            <Typography
              variant='h5'
              color='primary'
              gutterBottom
              sx={{
                color: theme.palette.natural.t,
              }}
            >
              {plan.price}
            </Typography>
            <Typography
              variant='body2'
              sx={{ mb: 2, color: theme.palette.natural.t2 }}
            >
              {plan.views} {t("extraCreditsPage.shows")}
            </Typography>
            <CustomButton
              title={t("extraCreditsPage.buttonText")}
              onClick={plan.onClick}
              size='medium'
              style={{
                padding: "0 16px",
              }}
            />
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};
